// src/api/safeFetch.ts
//
// Single fetch wrapper for ALL authenticated / backend-touching requests.
// The codebase had three patterns competing for "the right way to fetch":
//   1. configApi.request() — r.ok + throw (good), uses fetchWithAuth
//   2. raw fetch().then(r => r.json()) — silently swallowed 401 (the bug
//      that crashed /settings on 2026-06-14: the 401 body was stored as
//      stats and `stats.total_events.toLocaleString()` threw)
//   3. ad-hoc fetch() with `if (!r.ok) throw` — half-correct; threw but
//      did NOT dispatch the AUTH_401_EVENT the Router listens for
//
// This module is the canonical replacement. It:
//   - Throws on non-2xx (so callers' .catch runs)
//   - Dispatches AUTH_401_EVENT on 401, so the Router redirects to /login
//   - Sets credentials: 'include' by default (session cookie)
//
// The few public health/metrics endpoints (which don't require auth) can
// keep using a plain `fetch` — 401 on /health would be an outage, not a
// session problem. For everything that talks to the API, use this.

import { AUTH_401_EVENT, type Auth401EventDetail } from './authEvents';
import { readCookie } from './cookieUtil';

const CSRF_COOKIE = 'dash_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export class ApiError extends Error {
  status: number;
  detail?: string;
  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

function dispatch401(requestPath: string) {
  // The Router listener (router/routes.tsx) derives the round-trip
  // `from=` value from its closure-captured `path` state, NOT from
  // detail.from. We still set detail.from for diagnostics/observability
  // and to keep the Auth401EventDetail contract honest, but the
  // listener ignores it — see the CRITICAL comment in routes.tsx.
  // The request path is included in the event for tracing.
  const detail: Auth401EventDetail = {
    from: window.location.pathname,
  };
  // Also stash the request URL on the event for debugging — listener
  // doesn't read it, but tracing infrastructure can.
  (detail as any).requestPath = requestPath;
  window.dispatchEvent(new CustomEvent(AUTH_401_EVENT, { detail }));
}

export interface SafeFetchOptions extends Omit<RequestInit, 'body'> {
  /** Override the default credentials: 'include'. Set to 'omit' for
   *  public endpoints that must not send cookies. */
  credentials?: RequestCredentials;
  body?: BodyInit | null;
  /** If true, do NOT dispatch AUTH_401_EVENT on 401. Use this for
   *  /api/auth/status and /api/auth/login (the only endpoints that
   *  legitimately return 401 as part of their normal contract). */
  suppress401Event?: boolean;
}

export async function safeFetch<T = unknown>(
  path: string,
  init: SafeFetchOptions = {},
): Promise<T> {
  const { suppress401Event, credentials = 'include', ...rest } = init;
  const method = ((rest.method ?? 'GET') as string).toUpperCase();
  const headers = new Headers(rest.headers);
  // Mirror authClient.fetchWithAuth: on a non-safe method, attach the
  // dash_csrf cookie as X-CSRF-Token so the session-auth middleware
  // (backend/fastapi_pkg/_session_auth_wire.py: csrf_check) lets the
  // request through. Without this, every state-changing call (e.g. the
  // /runtime/transition POST from the Broker page) is rejected with 403
  // "CSRF token missing or invalid" — see 2026-06-15 broker-switch fix.
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  const res = await fetch(path, {
    ...rest,
    method,
    headers,
    credentials,
  });
  if (!res.ok) {
    // 401 is a session problem, not a one-off error: tell the Router so
    // it can navigate to /login. Suppress for auth-self endpoints.
    if (res.status === 401 && !suppress401Event) {
      dispatch401(path);
    }
    let detail: string | undefined;
    try {
      const j = await res.json();
      detail = typeof j?.detail === 'string' ? j.detail : undefined;
    } catch {
      // non-JSON body, fall through with statusText
    }
    throw new ApiError(
      res.status,
      detail || res.statusText || `HTTP ${res.status}`,
      detail,
    );
  }
  if (res.status === 204) return undefined as T;
  // .json() on an empty body rejects; guard with content-length.
  if (res.headers.get('content-length') === '0') return undefined as T;
  return (await res.json()) as T;
}

/** Convenience: GET + safeFetch + parse JSON. */
export function safeGet<T = unknown>(path: string, init?: SafeFetchOptions): Promise<T> {
  return safeFetch<T>(path, { ...init, method: 'GET' });
}
